import React from "react";
import { Tooltip } from "antd";
import { CheckCircleFilled, MinusCircleOutlined } from "@ant-design/icons";
import {
  CLIENT_STATUS_META,
  formatBool,
  formatDate,
  formatNumber,
  formatPlacement,
  formatTime,
  getClientStatus
} from "./health-clients-utils";
import { PERIOD_TYPE } from "./health-clients-constants";

const renderStatus = (_, row) => {
  const meta = CLIENT_STATUS_META[getClientStatus(row)];
  if (!meta) return "";
  return (
    <Tooltip title={meta.label}>
      <span className={`health-clients__status-dot ${meta.className}`} aria-label={meta.label} />
    </Tooltip>
  );
};

const renderFact = (onToggleFact) => (_, row) => {
  const isOk = Number(row.factIsOk) === 1;
  const label = isOk ? "Пришёл" : "Не пришёл";
  const Icon = isOk ? CheckCircleFilled : MinusCircleOutlined;
  return (
    <Tooltip title={`${label} · нажмите, чтобы изменить`}>
      <button
        type="button"
        className={`health-clients__fact-btn${isOk ? " health-clients__fact-btn--ok" : ""}`}
        aria-label={label}
        onClick={(e) => {
          e.stopPropagation();
          onToggleFact?.(row);
        }}
      >
        <Icon />
      </button>
    </Tooltip>
  );
};

export function buildHealthClientColumns(periodType, { onToggleFact } = {}) {
  const byExecutor = periodType === PERIOD_TYPE.EXECUTOR_DAY;

  const columns = [
    {
      title: "",
      key: "status",
      width: 40,
      align: "center",
      render: renderStatus
    },
    {
      title: "#",
      dataIndex: "idClient",
      key: "idClient",
      width: 64,
      className: "health-clients__col-card"
    },
    {
      title: "Клиент",
      dataIndex: "name",
      key: "name",
      width: 260,
      ellipsis: true,
      className: "health-clients__col-name"
    },
    {
      title: "Д.Р.",
      dataIndex: "dateBird",
      key: "dateBird",
      width: 100,
      render: formatDate
    },
    {
      title: "Путёвка",
      dataIndex: "namePut",
      key: "namePut",
      width: 150,
      ellipsis: true
    },
    {
      title: "Размещение",
      key: "docnum",
      width: 130,
      ellipsis: true,
      render: (_, row) => formatPlacement(row)
    },
    {
      title: "Заезд",
      dataIndex: "dateBeg",
      key: "dateBeg",
      width: 100,
      render: formatDate
    },
    {
      title: "Выезд",
      dataIndex: "dateEnd",
      key: "dateEnd",
      width: 100,
      render: formatDate
    },
    {
      title: "Лечащий врач",
      dataIndex: "sotrName",
      key: "sotrName",
      width: 130,
      ellipsis: true
    },
    {
      title: "Справ.",
      dataIndex: "isPred",
      key: "isPred",
      width: 56,
      align: "center",
      render: formatBool
    },
    {
      title: "Лечебная",
      dataIndex: "isOk",
      key: "isOk",
      width: 72,
      align: "center",
      render: formatBool
    },
    {
      title: "Внеш. #",
      dataIndex: "extraId",
      key: "extraId",
      width: 80,
      ellipsis: true
    },
    {
      title: "Регистрация",
      dataIndex: "dateReg",
      key: "dateReg",
      width: 96,
      render: formatDate
    },
    {
      title: "Дней",
      dataIndex: "days",
      key: "days",
      width: 64,
      align: "right",
      render: formatNumber
    },
    {
      title: "Выписка",
      dataIndex: "dateVipiska",
      key: "dateVipiska",
      width: 90,
      render: formatDate
    },
    {
      title: "Комментарий",
      dataIndex: "komment",
      key: "komment",
      width: 140,
      ellipsis: true
    },
    {
      title: byExecutor ? "Время" : "Визит",
      key: "visit",
      width: 90,
      render: (_, row) => (byExecutor ? formatTime(row.dateV) : formatDate(row.dateVizit))
    },
    {
      title: "Факт",
      dataIndex: "daysFact",
      key: "daysFact",
      width: 64,
      align: "right",
      render: formatNumber
    },
    {
      title: "Карта",
      dataIndex: "keyCardShort",
      key: "keyCardShort",
      width: 90,
      ellipsis: true
    },
    {
      title: "Депозит",
      dataIndex: "deposit",
      key: "deposit",
      width: 80,
      align: "right",
      render: formatNumber
    },
    {
      title: "Нет СК",
      dataIndex: "isNotSk",
      key: "isNotSk",
      width: 64,
      align: "center",
      render: formatBool
    },
    {
      title: "Основание",
      dataIndex: "osnTxt",
      key: "osnTxt",
      width: 100,
      ellipsis: true
    },
    {
      title: "Возраст",
      dataIndex: "age",
      key: "age",
      width: 72,
      align: "right",
      render: formatNumber
    },
    {
      title: "#док",
      dataIndex: "idDoc",
      key: "idDoc",
      width: 72,
      render: formatNumber
    },
    {
      title: "#стр",
      dataIndex: "idStr",
      key: "idStr",
      width: 56,
      render: formatNumber
    }
  ];

  if (byExecutor) {
    columns.push(
      {
        title: "Услуга",
        dataIndex: "tovName",
        key: "tovName",
        width: 140,
        ellipsis: true
      },
      {
        title: "Пришёл",
        dataIndex: "factIsOk",
        key: "factIsOk",
        width: 72,
        align: "center",
        render: renderFact(onToggleFact)
      },
      {
        title: "Кабинет",
        dataIndex: "zalName",
        key: "zalName",
        width: 120,
        ellipsis: true
      }
    );
  }

  columns.push(
    {
      title: "Вид лечения",
      dataIndex: "nameLech",
      key: "nameLech",
      width: 140,
      ellipsis: true
    },
    {
      title: "Соц. группа",
      dataIndex: "ssgName",
      key: "ssgName",
      width: 120,
      ellipsis: true
    },
    {
      title: "Заезд (док.)",
      dataIndex: "dDateBeg",
      key: "dDateBeg",
      width: 90,
      render: formatDate
    },
    {
      title: "Отъезд",
      dataIndex: "dDateEnd",
      key: "dDateEnd",
      width: 90,
      render: formatDate
    }
  );

  return columns;
}
